import React, { useEffect, useState, useRef } from "react";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import { useToasts } from "react-toast-notifications";
import "../../shared/GuidedInput.css";
import ConfirmResetModal from "../../shared/ConfirmResetModal";

const Input = (props) => {
  const { addToast } = useToasts();
  const [activeFieldIndex, setActiveFieldIndex] = useState(0);
  const [completedFields, setCompletedFields] = useState(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const inputRefs = useRef({});

  // Decimal fields use type="text" + inputMode="decimal" rather than
  // type="number": a native number input sanitises the in-progress value "3."
  // to "" on the decimal keystroke, so at normal typing speed the "." is
  // swallowed and decimals like 2.5 can never be entered.
  const fields = [
    { id: "direction", name: "direction", label: "View Direction", type: "number", step: 1 },
    { id: "radius", name: "radius", label: "Shaft Radius", type: "text", inputMode: "decimal" },
    { id: "dmax", name: "dmax", label: "Maximum Depth of Trace", type: "text", inputMode: "decimal" },
    { id: "dmin", name: "dmin", label: "Minimum Depth of Trace", type: "text", inputMode: "decimal" },
    { id: "dis", name: "dis", label: "Distance of Maximum Depth of Trace from Centerline", type: "text", inputMode: "decimal" },
    {
      id: "position",
      name: "position",
      label: "Position of Maximum Depth of Trace",
      type: "select",
      options: [
        { value: "pleft", label: "Left of Centerline" },
        { value: "pright", label: "Right of Centerline" },
      ],
    },
  ];

  const activeField = fields[activeFieldIndex];

  // Coming back from the output via "Edit Input" opens the form with every
  // field filled and editable.
  useEffect(() => {
    if (props.isEditInput) {
      setIsEditMode(true);
      setCompletedFields(new Set(fields.map((f) => f.id)));
    }
  }, [props.isEditInput]);

  useEffect(() => {
    if (!activeField || !inputRefs.current[activeField.id]) return;
    const el = inputRefs.current[activeField.id];
    const scrollPadding = 80;
    requestAnimationFrame(() => {
      el.focus();
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= scrollPadding && rect.bottom <= window.innerHeight - scrollPadding;
      if (!inView) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    });
  }, [activeFieldIndex, activeField]);

  const validateField = (fieldId) => {
    const dim = props.dimensions;
    let error = "";
    switch (fieldId) {
      case "direction":
        if (dim.direction === "" || dim.direction === undefined || dim.direction === null) {
          error = "Please enter view direction";
        } else if (dim.direction < 0 || dim.direction > 359) {
          error = "Please choose value between 0 and 359";
        }
        break;
      case "radius":
        if (!(parseFloat(dim.radius) > 0)) {
          error = "Shaft radius must be greater than 0";
        }
        break;
      case "dmax":
        if (!(parseFloat(dim.dmax) > 0)) {
          error = "Maximum depth must be greater than 0";
        }
        break;
      case "dmin":
        if (parseFloat(dim.dmin) < 0) {
          error = "Minimum depth cannot be negative";
        } else if (parseFloat(dim.dmin) >= parseFloat(dim.dmax)) {
          error = "Minimum depth must be less than maximum depth (" + dim.dmax + ")";
        }
        break;
      case "dis":
        if (parseFloat(dim.dis) < 0) {
          error = "Distance cannot be negative";
        }
        break;
      case "position":
        if (!dim.position) {
          error = "Please select a position";
        }
        break;
      default:
        break;
    }

    if (error) {
      if (window.audio) window.audio.play();
      addToast(error, {
        appearance: "error",
      });
      return false;
    }
    return true;
  };

  const handleFieldChange = (e) => {
    // The decimal fields are type="text" (so the in-progress "3." survives),
    // so re-impose numeric-only entry here: ignore keystrokes that would make
    // the value non-numeric while still allowing a trailing "." mid-typing.
    const field = fields.find((f) => f.name === e.target.name);
    if (field && field.inputMode === "decimal" && e.target.value !== "" && !/^\d*\.?\d*$/.test(e.target.value)) {
      return;
    }
    props.updatedimension(e);
  };

  const handleKeyDown = (e, fieldId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      tryAdvance(fieldId);
    }
  };

  const tryAdvance = (fieldId) => {
    if (validateField(fieldId)) {
      const nextCompleted = new Set([...completedFields, fieldId]);
      setCompletedFields(nextCompleted);
      if (activeFieldIndex < fields.length - 1) {
        setActiveFieldIndex(activeFieldIndex + 1);
      } else if (fields.every((f) => nextCompleted.has(f.id))) {
        const idx = getFirstInvalidFieldIndex();
        if (idx >= 0) {
          setActiveFieldIndex(idx);
          return;
        }
        if (isEditMode) setIsEditMode(false);
        props.showoutput();
      }
    }
  };

  const handleFieldClick = (index) => {
    if (isEditMode || index <= activeFieldIndex + 1) {
      setActiveFieldIndex(index);
    }
  };

  const isAllComplete = () => fields.every((f) => completedFields.has(f.id));

  const getFirstInvalidFieldIndex = () => {
    for (let i = 0; i < fields.length; i++) {
      if (!validateField(fields[i].id)) return i;
    }
    return -1;
  };

  const handleNext = () => {
    if (isEditMode) {
      const idx = getFirstInvalidFieldIndex();
      if (idx >= 0) {
        setActiveFieldIndex(idx);
        return;
      }
      setIsEditMode(false);
      props.showoutput();
      return;
    }

    if (activeField && !completedFields.has(activeField.id)) {
      if (!validateField(activeField.id)) return;
      setCompletedFields((prev) => new Set([...prev, activeField.id]));
    }

    const idx = getFirstInvalidFieldIndex();
    if (idx >= 0) {
      setActiveFieldIndex(idx);
      return;
    }

    props.showoutput();
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setActiveFieldIndex(0);
    setCompletedFields(new Set(fields.map((f) => f.id)));
  };

  // Reset Values asks first; only a confirmed reset clears the form.
  const [confirmingReset, setConfirmingReset] = useState(false);

  const handleReset = () => {
    props.resetvalues();
    setIsEditMode(false);
    setActiveFieldIndex(0);
    setCompletedFields(new Set());
  };

  const getFieldClass = (index, fieldId) => {
    if (isEditMode) {
      return index === activeFieldIndex
        ? "guided-field guided-field--active"
        : "guided-field guided-field--complete";
    }
    if (index === activeFieldIndex) return "guided-field guided-field--active";
    if (completedFields.has(fieldId)) return "guided-field guided-field--complete";
    return "guided-field guided-field--pending";
  };

  const renderField = (field, index) => {
    const isActive = index === activeFieldIndex;
    const isComplete = completedFields.has(field.id);

    return (
      <div key={field.id} className="guided-field-wrap">
        {isActive && !isComplete && (
          <span className="input-please">Input Please</span>
        )}
        <div
          className={getFieldClass(index, field.id)}
          onClick={() => handleFieldClick(index)}
        >
          <div className="guided-field__label">
            <span>{field.label}</span>
          </div>

          {field.type === "select" ? (
            <select
              ref={(el) => (inputRefs.current[field.id] = el)}
              id={field.id}
              name={field.name}
              className="guided-field__select"
              value={props.dimensions[field.name] ?? ""}
              onChange={handleFieldChange}
              onKeyDown={(e) => handleKeyDown(e, field.id)}
            >
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              ref={(el) => (inputRefs.current[field.id] = el)}
              type={field.type}
              inputMode={field.inputMode}
              id={field.id}
              name={field.name}
              className="guided-field__input"
              value={props.dimensions[field.name] ?? ""}
              onChange={handleFieldChange}
              onKeyDown={(e) => handleKeyDown(e, field.id)}
              step={field.step}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="page">
      <Header onBack={props.showobjective} />
      <Footer />
      <ConfirmResetModal
        show={confirmingReset}
        onCancel={() => setConfirmingReset(false)}
        onConfirm={() => {
          setConfirmingReset(false);
          handleReset();
        }}
      />
      <div className="page-content header-clear-medium">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card card-style contact-form">
              <div className="content guided-uniform">
                {fields.map((field, index) => renderField(field, index))}

                <div className="guided-input-actions mt-4">
                  <button
                    type="button"
                    className="btn bg-red-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={() => setConfirmingReset(true)}
                  >
                    Reset Values
                  </button>
                  <button
                    type="button"
                    className="btn bg-secondary text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn bg-green-dark text-uppercase font-900 btn-m rounded-sm shadow-xl"
                    onClick={handleNext}
                    disabled={!isEditMode && !isAllComplete() && activeFieldIndex < fields.length - 1}
                  >
                    {props.label || "Next"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Input;
